import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  styled
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { showErrorToast, showSuccessToast } from '../../components/Toast/actions';
import { $post, $get } from '../../utils/http';

const SoftwareUpdates: React.FC = () => {
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState('');
  const [version, setVersion] = useState('');
  const [releaseNotesList, setReleaseNotesList] = useState<{ releasenote: string; version: string }[]>([]);

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        const data = await $get('/releaseNote/all');
        if (Array.isArray(data)) {
          setReleaseNotesList(data);
        } else {
          setReleaseNotesList([]);
        }
      } catch (error) {
        showErrorToast({
          title: 'Error',
          subtitle: 'Failed to fetch release notes'
        });
        setReleaseNotesList([]);
      }
    };

    fetchReleaseNotes();
  }, []);

  const handleSaveNote = async () => {
    try {
      await $post('/dashboard/systemUpdate', {
        releaseNote: releaseNotes,
        releaseVersion: version
      });

      const newNote = { releasenote: releaseNotes, version };
      setReleaseNotesList(prevNotes => [...prevNotes, newNote]);
      setReleaseNotes('');
      setVersion('');
      setNoteDialogOpen(false);

      showSuccessToast({
        title: 'Success',
        subtitle: 'Release note added successfully'
      });
    } catch (error) {
      showErrorToast({
        title: 'Error',
        subtitle: 'Failed to save release note'
      });
    }
  };

  return (
    <Wrapper>
      <Header>
        <Typography variant="h5">RELEASE NOTES</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNoteDialogOpen(true)}
          sx={{
            backgroundColor: '#9CC740',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#8BB534'
            }
          }}>
          Add Release Note
        </Button>
      </Header>

      <Content>
        <ReleaseNotesContainer>
          {releaseNotesList.map((note, index) => (
            <ReleaseNoteCard key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Version {note.version}
                </Typography>
              </Box>
              <Typography color="text.secondary">{note.releasenote}</Typography>
            </ReleaseNoteCard>
          ))}
        </ReleaseNotesContainer>
      </Content>

      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a Release Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Version Number"
            type="text"
            fullWidth
            value={version}
            onChange={e => setVersion(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Release Note"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={releaseNotes}
            onChange={e => setReleaseNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveNote} variant="contained" sx={{ backgroundColor: '#9CC740' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  height: 100%;
  background-color: #fff;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const Header = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Content = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3, 6)};
  flex: 1;
  min-height: 0;
`;

const ReleaseNotesContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ReleaseNoteCard = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  background-color: #fff;
`;

export default SoftwareUpdates;
